/**
 * Taps on a bench, on an iPhone.
 *
 * iOS Safari, on a page with no touch listener anywhere, did not turn a tap on
 * the optics bench into the pointer events the bench listens to — so no part
 * could be selected — while the same bench in the student app, and the
 * chemistry lab on the same site, took taps fine. The difference was only
 * whether SOMETHING on the page listened for touches: the student app has a
 * window `touchstart` of its own, and the chemistry lab's long-press
 * (`useLongPress`) listens to touch events as a side effect of its job. On
 * zitmus the optics bench had none, and adding one — first by accident, in a
 * debug log, then on purpose — was the whole fix.
 *
 * So every bench registers one here, rather than each depending on whether it
 * happens to listen for touches for some other reason. The listener does
 * nothing and is passive, so it costs nothing and blocks no scroll; it only has
 * to exist while the bench is on screen.
 */
import { useEffect } from 'react';

const TOUCHES = ['touchstart', 'touchend', 'touchcancel'] as const;
const OPTS = { capture: true, passive: true } as const;

export function useBenchTouch(): void {
    useEffect(() => {
        const noop = (): void => {};
        TOUCHES.forEach(t => window.addEventListener(t, noop, OPTS));
        return () => TOUCHES.forEach(t => window.removeEventListener(t, noop, OPTS));
    }, []);
}
